import { SkeletonLoader } from '@toggle/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { useEarningsInsightDrawer } from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { EarningsDateLabel } from '../../components/earnings-date-label/EarningsDateLabel';
import { PublishedDate } from '../../components/published-date/PublishedDate';
import { EarningsExpectationsTable } from './earnings-expectations-table/EarningsExpectationsTable';
import * as S from './PreviewOverview.styles';
import { TargetPriceSection } from './target-price-section/TargetPriceSection';

export interface PreviewOverviewProps {
  title: string;
  activeEntityTag: string;
  publishedAt: string;
}

export const PreviewOverview = ({
  title,
  activeEntityTag,
  publishedAt,
}: PreviewOverviewProps) => {
  const { t } = useTranslation(['earnings']);
  const { earningsId } = useEarningsInsightDrawer();

  const {
    data: earningsData,
    isFetching,
    isError,
    error,
  } = useGetEarningById(earningsId ?? '');

  if (isFetching) {
    return (
      <S.StyledLoaderWrapper>
        <SkeletonLoader areas={S.styledLoaderAreas} />
      </S.StyledLoaderWrapper>
    );
  }

  if (isError) {
    throw new Error(error?.message);
  }

  return (
    <>
      <PublishedDate publishedAt={publishedAt} isPreview />
      <S.Title>{title}</S.Title>
      <S.Wrapper>
        {earningsData && (
          <>
            <EarningsDateLabel
              date={earningsData?.reporting_date}
              label={t(
                'earningsInsight.drawer.earningsPreview.earningsReleased'
              )}
            />
            <EarningsExpectationsTable earningsData={earningsData} />
          </>
        )}
        <ErrorBoundaryWrapper>
          <TargetPriceSection entityTag={activeEntityTag} />
        </ErrorBoundaryWrapper>
      </S.Wrapper>
    </>
  );
};
